// Global HTTP 429 handling: retry with backoff, then tell the user once and
// keep the rejection from surfacing as an uncaught page error.

export const RATE_LIMIT_MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 10000;

const listeners = new Set();

export const isRateLimitError = (error) => error?.response?.status === 429;

/**
 * Delay before retry number `attempt` (0-based). Honors a numeric Retry-After
 * header (seconds) and otherwise backs off exponentially, capped at 10s.
 */
export const rateLimitRetryDelay = (error, attempt) => {
  const header = error?.response?.headers?.["retry-after"];
  const seconds = Number(header);
  if (header != null && header !== "" && Number.isFinite(seconds) && seconds >= 0) {
    return Math.min(seconds * 1000, MAX_DELAY_MS);
  }
  return Math.min(BASE_DELAY_MS * 2 ** attempt, MAX_DELAY_MS);
};

export const onRateLimited = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const notifyRateLimited = (error) => {
  listeners.forEach((listener) => {
    try {
      listener(error);
    } catch {
      // a broken listener must not turn into another page error
    }
  });
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Adds 429 retry/backoff to an axios instance. When retries run out the error
 * is marked `rateLimited`, listeners are notified, and the promise still rejects
 * so callers can stop their spinners.
 */
export const attachRateLimitRetry = (instance, { maxRetries = RATE_LIMIT_MAX_RETRIES, sleep = wait } = {}) => {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (!isRateLimitError(error) || !error.config) {
        return Promise.reject(error);
      }

      const attempt = error.config.__rateLimitAttempt ?? 0;
      if (attempt < maxRetries) {
        await sleep(rateLimitRetryDelay(error, attempt));
        return instance.request({ ...error.config, __rateLimitAttempt: attempt + 1 });
      }

      error.rateLimited = true;
      notifyRateLimited(error);
      return Promise.reject(error);
    },
  );
  return instance;
};

/**
 * Rate-limited requests have already been reported to the user, so a caller
 * that forgot `.catch` must not crash the page.
 */
export const installRateLimitRejectionGuard = (target = window) => {
  const handler = (event) => {
    if (event?.reason?.rateLimited) {
      event.preventDefault();
    }
  };
  target.addEventListener("unhandledrejection", handler);
  return () => target.removeEventListener("unhandledrejection", handler);
};
